// 発注メール文面ジェネレーター — supplier-facing 発注 email body.
// PILOT C (Communication Reviewer, Jun 2026): the app used to record only
// via/to when a PO was "sent"; the buyer had to write the mail from scratch.
// This composes a ready-to-send 日本語ビジネス文 (件名/挨拶/依頼内容/発注番号/
// 納期/回答依頼/署名) for copy-to-clipboard or mailto. Nothing is sent from here.

const yen = (n) => `¥${Math.round(Number(n) || 0).toLocaleString('ja-JP')}`;
const jd = (v) => (v ? String(v).slice(0, 10).replace(/-/g, '/') : '');

const SIGN_DEFAULT = {
  company: '株式会社センスビー',
  dept: '資材部',
  tel: '',
  email: '',
};

export function buildOrderMailSubject(order) {
  const no = order?.order_no || '';
  const head = (order?.lines?.[0]?.name || order?.lines?.[0]?.part_no || '').trim();
  const more = (order?.lines?.length || 0) > 1 ? ` 他${order.lines.length - 1}件` : '';
  return `【発注書送付】${no}${head ? ` ${head}${more}` : ''}`;
}

/**
 * @param {object} order  decorated shizai order
 * @param {object} opts   { senderName, senderEmail, tel, company, dept, replyBy }
 * @returns {string} plain-text mail body
 */
export function buildOrderMailBody(order, opts = {}) {
  const sign = { ...SIGN_DEFAULT, ...opts };
  const supplier = order?.supplier_name || order?.supplier?.name || '';
  const contact = order?.supplier?.contact_name ? `${order.supplier.contact_name} 様` : 'ご担当者様';
  const lines = order?.lines || [];
  const due = jd(order?.due_date);
  const replyBy = jd(opts.replyBy);
  const dest = order?.delivery_destination_name || order?.delivery_place || '';

  const items = lines.map((l, i) => {
    const name = l.name || l.part_no || '—';
    const spec = [l.part_no && l.name ? `品番 ${l.part_no}` : '', l.drawing_no ? `図番 ${l.drawing_no}` : '',
      l.maker ? `メーカー ${l.maker}` : ''].filter(Boolean).join(' / ');
    return `${i + 1}. ${name}\n   数量: ${l.qty || 0} ${l.uom || ''}${spec ? `\n   ${spec}` : ''}`;
  }).join('\n');

  return [
    supplier ? `${supplier} ${contact}` : contact,
    '',
    'いつもお世話になっております。',
    `${sign.company} ${sign.dept}${sign.senderName ? ` ${sign.senderName}` : ''} です。`,
    '',
    '下記のとおり発注いたしますので、ご手配をお願いいたします。',
    '発注書を添付（別送）いたしますので、あわせてご確認ください。',
    '',
    '■ 発注内容',
    `発注番号: ${order?.order_no || ''}`,
    `発注日: ${jd(order?.order_date) || '—'}`,
    items || '（明細なし）',
    order?.total ? `合計金額: ${yen(order.total)}（税込）` : null,
    '',
    '■ 納期・納入先',
    `希望納期: ${due || '別途ご相談'}`,
    dest ? `納入先: ${dest}` : null,
    order?.notes ? `備考: ${order.notes}` : null,
    '',
    '■ ご回答のお願い',
    `${replyBy ? `${replyBy}まで` : '3営業日以内'}に、下記2点をご返信ください。`,
    '　1. 本発注の受注可否',
    '　2. 確定納期（希望納期に添えない場合は最短納期）',
    '',
    'ご不明な点がございましたら、下記までご連絡ください。',
    'よろしくお願いいたします。',
    '',
    '――――――――――――――――――',
    sign.company,
    sign.dept,
    sign.senderName || null,
    sign.tel ? `TEL: ${sign.tel}` : null,
    sign.senderEmail || sign.email ? `MAIL: ${sign.senderEmail || sign.email}` : null,
    '――――――――――――――――――',
  ].filter((l) => l !== null && l !== undefined).join('\n');
}

export function buildOrderMailto(order, opts = {}) {
  const to = opts.to || order?.supplier?.contact_email || '';
  const subject = encodeURIComponent(buildOrderMailSubject(order));
  const body = encodeURIComponent(buildOrderMailBody(order, opts));
  return `mailto:${encodeURIComponent(to)}?subject=${subject}&body=${body}`;
}
